/**
 * **Search — the stealth-countering action's rules core.** Pure rules (no I/O, no dice): the
 * line-of-sight precondition the gameplay Search is gated by, sibling to Hide's
 * {@link checkHideCover} (`hide.ts`). The Sensors
 * range gate reuses `checkTechRange` (`economy.ts`) as-is — this module supplies only the LoS leg,
 * the one gate Search needs that Hide's own gates don't already cover. The room
 * (`backend/.../encounterRoom.ts`) composes budget → Sensors → this LoS check → `resolveContest`
 * (`contest.ts`) → `clearHiddenSteps` (`hide.ts`) into the Search resolution.
 *
 * **LoS only, not cover** — unlike Hide's cover auto-check (which rejects Hide when a hostile has
 * a fully clear, uncovered line), Search's RAW precondition is just "line of sight to the target"
 * (Core Book p.74: "within sensor range and can see"). A target behind cover is still a legal
 * Search target as long as the sightline isn't fully blocked — the same `visible`-only reading the
 * `line_of_sight` spatial predicate uses (`effects/spatialAnswerer.ts`), reusing the identical
 * `terrainVisibility` geometry `hide.ts` already reuses. Never reimplement the geometry.
 */

import {
  type EncounterState,
  worldFor,
  terrainVisibility,
} from "@lancer-vtt/domain";
import { type Result, ok, err } from "@lancer-vtt/shared";
import { footprintCells, sightCut } from "./effects/spatialAnswerer";

/**
 * The shared LoS geometry {@link checkSearchLoS}, Scan's `checkTechLoS`, and `economy.ts`'s
 * `checkAttackLoS` all gate on — factored out and neutrally named (not Search-private) so every
 * LoS-gated verb's error strings stay honest (`"search: …"` vs `"scan: …"` vs `"attack: …"`)
 * without duplicating the `terrainVisibility` call. Never reimplement the geometry at a fourth
 * call site.
 */
export function checkLineOfSight(args: { state: EncounterState; viewer: string; target: string; verb: string }): Result<void> {
  const { state, viewer, target, verb } = args;
  const s = state.combatants[viewer];
  if (!s) return err(`${verb}: ${viewer} is not on the map`);
  const t = state.combatants[target];
  if (!t) return err(`${verb}: ${target} is not on the map`);
  if (!s.pos) return err(`${verb}: ${s.name} is not placed on the map (can't measure line of sight)`);
  if (!t.pos) return err(`${verb}: ${t.name} is not placed on the map (can't measure line of sight)`);

  const world = worldFor(state);
  const from = footprintCells(world, s);
  const to = footprintCells(world, t);
  const cut = sightCut(state, s, to);
  if (cut !== undefined) return err(`${verb}: ${cut}`);
  const view = terrainVisibility({ space: world, terrain: state.terrain, from, to, edges: state.edges, combatants: state.combatants });
  if (!view.visible) {
    return err(`${verb}: ${s.name} has no line of sight to ${t.name}`);
  }
  return ok(undefined);
}

/**
 * The **line-of-sight precondition** Search is gated by: the searcher must have an
 * unblocked sightline to the target (`terrainVisibility(...).visible`) — cover on that line
 * doesn't matter, only a fully blocked line rejects. Both combatants must be placed on the map
 * (an unplaced searcher/target can't be measured). Pure over `state`: reads positions/terrain,
 * never mutates. Returns the shared {@link Result} verdict the room surfaces (a rejection is a
 * logged no-op before any dice are drawn).
 */
export function checkSearchLoS(state: EncounterState, searcher: string, target: string): Result<void> {
  return checkLineOfSight({ state, viewer: searcher, target, verb: "search" });
}

/**
 * The player-facing verbs {@link checkTechLoS} gates — the message-prefix each rejection uses
 * (`checkLineOfSight`'s `verb` is display-only, never parsed/asserted-on structurally). A
 * **closed union**, grown per demand (same house pattern as `StatScaledAmount.stat`) — do NOT
 * widen to `string`.
 */
export type TechLoSVerb = "scan" | "lock on" | "invade";

/**
 * **Tech-attack/activate line-of-sight precondition**: RAW Scan/Lock On/Invade (Core
 * p.64/283, SRD L480/L488) need "within SENSORS and line of sight" — unlike Bolster, which is
 * SENSORS-only (`seedEntries.ts`'s SCAN doc comment, SRD L464). Same geometry as
 * {@link checkSearchLoS}, `verb`-prefixed so a rejection names the right action (`"scan:"`,
 * `"lock on:"`, `"invade:"`). The room composes this alongside `checkTechRange` (`economy.ts`)
 * before resolving each gated tech action, mirroring how it composes Sensors-range + this LoS
 * check for Search.
 */
export function checkTechLoS(args: { state: EncounterState; actor: string; target: string; verb: TechLoSVerb }): Result<void> {
  const { state, actor, target, verb } = args;
  return checkLineOfSight({ state, viewer: actor, target, verb });
}
